import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { CelebrationArt, RedirectTimerBar, formatConfirmedDate, getConfirmedDetails, parseDuration } from "./BookingConfirmed";

const SEAM_STYLE = { backgroundImage: "url('/assets/checkout/booking-confirmed/seam.svg')", backgroundRepeat: "repeat-x", backgroundSize: "auto 100%" };
const DASHED_STYLE = { backgroundImage: "repeating-linear-gradient(to right, #d1d5db 0, #d1d5db 6px, transparent 6px, transparent 12px)" };

const StopRow = ({ label, labelColor, location, cityname, dateStr }) => (
  <div className="flex flex-col gap-1 items-start w-full min-w-0">
    <div className="flex items-center justify-between gap-2 w-full">
      <span className={`block ${labelColor} text-[clamp(11px,3.2vw,13px)] font-bold uppercase tracking-wide text-left`}>{label}</span>
      <span className="block text-[#111827] text-[clamp(12.5px,3.6vw,15px)] font-semibold whitespace-nowrap">{formatConfirmedDate(dateStr)}</span>
    </div>
    <span className="block text-[#111827] text-[clamp(13px,3.6vw,15px)] font-bold leading-5 text-left break-words max-w-full">{location || "Hub Location"}</span>
    {cityname && <span className="block text-[#6b7280] text-[12px] leading-4 text-left">{cityname}</span>}
  </div>
);

/* ─── mobile (<768px) booking confirmation; all state and handlers stay in Checkout ───
   No Figma frame exists for this screen: adapted from the desktop popup (node 435:9229).
   Portalled and above z-index 16000160 so the chat and WhatsApp widgets can't cover the buttons. */
const BookingConfirmedMob = ({ paymentDone, carData, savedImageUrl, savedBodyType, formattedStartDate, formattedEndDate, duration, cityname, pickup, drop, bookingCode, payableAmount, onViewBooking, onBackHome }) => {
  const { bookingId, vehicleName, vehicleSpecs, carImg, totalPaid } = getConfirmedDetails({ paymentDone, carData, savedImageUrl, savedBodyType, bookingCode, payableAmount });
  const { full, headline } = parseDuration(duration);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, []);

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Booking Confirmed" className="fixed inset-0 z-[16000161] bg-white flex flex-col font-['Outfit',sans-serif] text-left">

      <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-4 pb-5">
        <div className="flex flex-col gap-3 items-center justify-center min-h-full w-full max-w-[480px] mx-auto">
          <div className="flex flex-col gap-2 items-center">
            <CelebrationArt />
            <h1 className="m-0 text-[#111827] text-[clamp(24px,7.2vw,30px)] font-extrabold leading-tight text-center">Booking Confirmed!</h1>
          </div>

          {/* Itinerary card */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-[0px_1px_1.5px_rgba(0,0,0,0.06),0px_4px_3px_rgba(0,0,0,0.05)] flex flex-col gap-3 p-[clamp(12px,3.9vw,16px)] w-full min-w-0">

            <div className="flex gap-3 items-center w-full">
              <div className="w-[40%] max-w-[170px] aspect-[188/95] shrink-0 overflow-hidden">
                <img src={carImg} alt={vehicleName} className="w-full h-full object-contain" />
              </div>
              <div className="flex flex-1 flex-col gap-1 items-start min-w-0">
                <span className="block text-[#111827] text-[clamp(14px,4.1vw,17px)] font-bold leading-tight text-left break-words max-w-full">{vehicleName}</span>
                {vehicleSpecs && <span className="block text-[#6b7280] text-[clamp(11px,3.1vw,13px)] leading-4 text-left">{vehicleSpecs}</span>}
                <span className="block bg-[#e6f4f1] border-[1.5px] border-[#0bab7c] rounded-[4px] px-2 py-0.5 mt-0.5 max-w-full text-[#0bab7c] text-[clamp(11px,3.1vw,13px)] font-bold break-all text-left">ID: #{bookingId}</span>
              </div>
            </div>

            <div className="w-full h-px" style={DASHED_STYLE} />

            <StopRow label="Pickup" labelColor="text-[#0bab7c]" location={pickup} cityname={cityname} dateStr={formattedStartDate} />

            <div className="flex items-center w-full">
              <div className="flex-1 h-px" style={SEAM_STYLE} />
              <div className="bg-[rgba(128,128,254,0.64)] px-2 py-0.5 rounded-[8px] mx-2 min-w-[82px] flex items-center justify-center">
                <span className="block text-[#000646] text-[clamp(11.5px,3.2vw,13px)] font-bold whitespace-nowrap">{full}</span>
              </div>
              <div className="flex-1 h-px" style={SEAM_STYLE} />
            </div>

            <StopRow label="Drop Off" labelColor="text-[#ef4444]" location={drop} cityname={cityname} dateStr={formattedEndDate} />

            <hr className="border-[#e5e7eb] w-full m-0" />

            <div className="flex items-center justify-between gap-2 w-full">
              <div className="bg-[rgba(128,128,254,0.1)] px-2.5 py-1 rounded-[6px] shrink-0">
                <span className="block text-[#0bab7c] text-[clamp(15px,4.4vw,18px)] font-bold whitespace-nowrap">{headline}</span>
              </div>
              <div className="flex flex-col gap-0.5 items-end min-w-0">
                <span className="block text-[#6b7280] text-[12px] leading-4">Total Amount Paid</span>
                <span className="block text-[#0bab7c] text-[clamp(20px,5.8vw,24px)] font-extrabold leading-7 whitespace-nowrap">₹{totalPaid}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons stay on screen however long the itinerary gets */}
      <div className="shrink-0 bg-white border-t border-[#f3f4f6] px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]">
        <div className="flex gap-[clamp(8px,2.9vw,12px)] w-full max-w-[480px] mx-auto">
          <button
            type="button"
            onClick={onViewBooking}
            className="relative flex-1 min-w-0 h-[50px] flex items-center justify-center rounded-[12px] text-white text-[clamp(12.5px,3.6vw,15px)] font-semibold overflow-hidden"
            style={{ background: "linear-gradient(9.68deg, #9e85dd 25%, #7b39ec 59.86%)" }}
          >
            View Booking Details
            <RedirectTimerBar />
          </button>
          <button
            type="button"
            onClick={onBackHome}
            className="flex-1 min-w-0 h-[50px] flex items-center justify-center rounded-[12px] bg-white border-[1.5px] border-[#0bab7c] text-[#0bab7c] text-[clamp(12.5px,3.6vw,15px)] font-bold"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BookingConfirmedMob;
